// src/pages/Pricing.jsx
// Public membership pricing page. Built from the owner's design mock; all styling lives in
// styles/Pricing.css (scoped under .pricing-page) rather than MUI sx, because the mock is a
// large custom design with its own type scale, keyframes, and grid.
//
// Prices are hardcoded here on purpose. The existing /api/quotes/pricing endpoint and
// AdminPricingPanel are book-printing-specific and have no concept of membership tracks,
// so backing this with the API would need new schema + endpoints (separate story).
//
// Each track now carries one free tier and one paid "Access" tier (not three paid tiers) —
// they render as the "dyn-card" pairs below the Why/Rights sections. Everything derives from
// TRACKS/INSTITUTION so a price or feature change can't desync across the ticker, service
// cards, dyn-cards, and join row — edit the data, not the markup.
//
// The top nav and footer intentionally do NOT come from the design mock — they mirror
// Home.jsx's tabs (see About.jsx for the same convention) and the shared <HomeFooter />.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Pricing.css';

const TRACK_ICON_PATHS = {
  reader: ['M10 25 Q35 15 50 25 Q65 15 90 25 L90 78 Q65 68 50 78 Q35 68 10 78 Z', 'M50 25 L50 78'],
  collaborator: [
    'M50 15 C42 15 40 22 44 27 C38 30 34 36 36 42 L34 60 L66 60 L64 42 C66 36 62 30 56 27 C60 22 58 15 50 15 Z',
    'M28 78 L72 78 L68 60 L32 60 Z',
  ],
  author: ['M85 12 L92 19 L38 73 L27 76 L30 65 Z', 'M78 19 L85 26'],
};

const TrackIconBg = ({ id }) => (
  <div className="dyn-icon-bg">
    <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2.2">
      {TRACK_ICON_PATHS[id].map((d, i) => <path key={i} d={d} />)}
    </svg>
  </div>
);

const TRACKS = [
  {
    id: 'reader',
    roman: 'I.',
    num: 'Track I',
    name: 'Reader',
    role: 'The Subscriber',
    cardBlurb:
      'The avid consumer of knowledge — purchasing and consuming literary works, with the ability to shape future editions and see into an author’s process.',
    free: {
      name: 'Bookmark',
      role: 'Casual Browser',
      ctaLabel: 'Get started free →',
      feats: [
        'Unlimited ReVisionari Classics (13,000+ titles)',
        'Free preview chapters of new releases',
        'Follow authors & new release alerts',
      ],
    },
    paid: {
      name: 'Reader Access',
      role: 'Full Membership',
      price: '$4.99',
      ctaLabel: 'Get started →',
      feats: [
        'Everything in Bookmark',
        'Full new-release catalogue access',
        'Unlimited direct author messaging',
        'Community posting + physical books at cost',
      ],
    },
    dynClass: 'dyn-card--reader',
  },
  {
    id: 'collaborator',
    roman: 'II.',
    num: 'Track II',
    name: 'Collaborator',
    role: 'The Curator',
    cardBlurb:
      'A highly engaged subject-matter expert or developmental editor — hired by authors for critical feedback, with zero commission on what you’re paid.',
    free: {
      name: 'Apprentice',
      role: 'Prospective Collaborator',
      ctaLabel: 'Join free →',
      capIndex: 2,
      feats: [
        'Public reviewer profile (unverified)',
        '3 feedback submissions / month',
        'Create up to 3 unpublished covers',
        'Browse open author review requests',
      ],
    },
    paid: {
      name: 'Collaborator Access',
      role: 'Verified Editorial Partner',
      price: '$7.99',
      ctaLabel: 'Join as collaborator →',
      capIndex: 1,
      feats: [
        'Verified profile & reputation ranking',
        'Up to 30 concurrent unpublished covers',
        'Priority author matching',
        'Paid gigs, zero commission ever',
      ],
    },
    dynClass: 'dyn-card--collab',
  },
  {
    id: 'author',
    roman: 'III.',
    num: 'Track III',
    name: 'Author',
    role: 'The Visionary',
    cardBlurb:
      'The primary creator — writing independently, then publishing à la carte when ready, No-Frills or Full-Service.',
    free: {
      name: 'Draft',
      role: 'Prospective Author',
      ctaLabel: 'Start writing free →',
      capIndex: 1,
      feats: [
        'Manuscript editor & Story Flow outlining',
        'Create up to 3 projects',
        'Draft author profile page',
        'Browse the Collaborator marketplace',
      ],
    },
    paid: {
      name: 'Author Access',
      role: 'Full Membership',
      price: '$29.99',
      ctaLabel: 'Start writing →',
      capIndex: 1,
      feats: [
        'Everything in Draft',
        'Up to 50 concurrent projects',
        'Unlimited messaging, full analytics',
        'Priority Collaborator matching & hiring',
      ],
    },
    dynClass: 'dyn-card--author',
  },
];

const INSTITUTION = {
  id: 'institution',
  num: 'Track IV',
  name: 'Institution',
  role: 'Schools & Organizations',
  cardBlurb:
    'Any organization looking to bring its people into the literary process — schools and universities, publishers, libraries, or rehabilitative and correctional programs.',
  plevels: [
    { label: 'Per-seat workspace', value: '$15/seat/mo' },
    { label: 'Minimum commitment', value: '20 seats' },
  ],
  blurb:
    'Any organization looking to bring its people into the literary process gains a managed workspace with seat-based billing, unified analytics, and dedicated support — schools and universities, publishers and libraries, or rehabilitative and correctional programs, with access to all three tracks and tier levels.',
  feats: [
    'Managed team workspace',
    'All role tiers included',
    'Admin & permissions control',
    'Unified analytics dashboard',
    'Dedicated support',
    'Custom billing & invoicing',
  ],
  price: '$15',
  per: '/ seat / month',
  matrixSummary:
    'All roles & tiers included · dedicated support · unified analytics · $15/seat/mo · 20-seat minimum ($300/mo min.)',
};

// "The old way" vs. a ReVisionari membership — last row styled like the institution rows
// elsewhere on the page (dark, gold-on-purple) to close the section on the strongest case.
const WHY_ROWS = [
  {
    label: 'Write & format',
    old: 'Vellum ($199.99–$249.99 one-time, Mac-only) or Atticus ($147 one-time)',
    withRV: 'Included in every Author account — plus your collaborators and readers are already here',
  },
  {
    label: 'Editorial feedback',
    old: 'Hire freelance editors blind, or trade favors in writing groups',
    withRV: 'Built-in Collaborator marketplace with a public reputation & ranking system',
  },
  {
    label: 'Cover design',
    old: 'Freelance sites, no vetting, $200–$400 and hope',
    withRV: 'Direct marketplace access to Collaborators already active on the platform',
  },
  {
    label: 'ISBNs & copyright',
    old: 'ISBN from Bowker ($125 single) plus a separate U.S. Copyright Office filing ($45–$85) — two vendors, two checkouts',
    withRV: 'Both bundled into one Publish Package ($129.99 No-Frills or $199.99 Full-Service) — no separate vendor, no separate filing',
  },
  {
    label: 'Classic literature',
    old: 'Project Gutenberg — free, but a dated, utilitarian reading experience with no community around it',
    withRV: '13,000+ public-domain classics in ReVisionari Classics, in the same clean reader experience as new releases — free on every account',
  },
  {
    label: 'Reader relationship',
    old: 'A mailing list, social media, and a separate fan-subscription tool (Patreon takes ~10–16% of everything earned there)',
    withRV: 'Native author messaging & reader tiers at a flat monthly cost — no revenue cut',
  },
  {
    label: 'Audiobook',
    old: 'Shop it out separately — often hundreds to thousands of dollars, no built-in trust signal',
    withRV: 'Marketplace access to vetted narrators & audio engineers, already inside your workflow',
    institution: true,
  },
];

const RIGHTS_TIERS = [
  {
    id: 'no-frills',
    flag: 'No-Frills',
    flagStyle: { background: 'var(--purple-soft)' },
    name: 'No-Frills Publish',
    role: 'For self-sufficient authors',
    price: '$129.99',
    per: 'one-time',
    desc: 'ISBN, copyright filing, and a compliance check — nothing else. Built for authors who already have their own editing and marketing covered.',
    feats: ['ISBN registration (1 format)', 'U.S. Copyright Office filing', 'Baseline compliance/risk check'],
    locked: 'No editorial review, no marketing package',
    royalty: '15%',
    term: '24 months',
    cta: 'Publish No-Frills →',
  },
  {
    id: 'full-service',
    featured: true,
    flag: 'Full-Service',
    name: 'Full-Service Publish',
    role: 'For the complete managed experience',
    price: '$199.99',
    per: 'one-time',
    desc: 'Everything in No-Frills, plus a full editorial/legal review and a guaranteed marketing push — ReVisionari actively publishes and distributes your book.',
    feats: [
      'Everything in No-Frills',
      'Full editorial & legal review',
      '60-day featured placement + newsletter + social spotlight',
      'Free-tier catalogue rotation',
    ],
    royalty: '35%',
    term: '24 months',
    cta: 'Publish Full-Service →',
  },
];

const RIGHTS_STATS = [
  {
    num: '65%',
    lbl: 'Full-Service author keeps',
    text: "Beats Amazon's own Kindle Press imprint deal (50/50) and typical hybrid publishers (~50/50) by 15 points.",
  },
  {
    num: '85%',
    lbl: 'No-Frills author keeps',
    text: 'Only a 15% royalty for the convenience of publishing through the platform you already write in — the rest is yours.',
  },
  {
    num: '24',
    unit: 'mo',
    lbl: 'Exclusive term, either path',
    text: "One consistent term regardless of which path you choose — under half of Kindle Press's 5-year term and a third of ACX's 7-year audiobook lock.",
  },
  {
    num: '12',
    unit: 'mo',
    lbl: 'Early reversion trigger',
    text: "If trailing 12-month royalties fall under $250, either side can request reversion — twice as fast as Kindle Press's comparable $500/12-month clause.",
  },
];

const RIGHTS_TABLE = [
  { label: 'Amazon KDP (direct)', keeps: '~65–70%', term: 'None required', exit: 'Leave anytime' },
  { label: 'Traditional Big 5', keeps: '~10–25%', term: 'Life of copyright', exit: 'Almost never triggers' },
  { label: 'Kindle Press (Amazon)', keeps: '50%', term: '5 years, renewable', exit: 'Reversion after 2 yrs if under $500/yr' },
  { label: 'ACX audiobook, exclusive', keeps: '40–50%', term: '7 years, renewable', exit: 'No early exit' },
  {
    label: 'ReVisionari — No-Frills',
    keeps: '85%',
    term: '24 months',
    exit: 'Reversion after 12 mo if under $250/yr',
    institution: true,
  },
  {
    label: 'ReVisionari — Full-Service',
    keeps: '65%',
    term: '24 months',
    exit: 'Reversion after 12 mo if under $250/yr',
    institution: true,
  },
];

// The marquee needs its content twice over — the CSS animation translates the track by -50%,
// so the second copy is what makes the loop seamless.
const TICKER_ITEMS = [
  ...TRACKS.map((t) => ({ roman: t.roman, text: `${t.name} — ${t.role.toLowerCase()}` })),
  { text: '3 tracks · 2 tiers each' },
  { text: 'Free tier on every track' },
  { text: 'Authors keep 65% of royalties' },
  { text: 'Institution plan available' },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user, startCognito } = useAuth();
  const rootRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);

  // Already-logged-in visitors skip the Cognito redirect entirely and go straight in.
  const handleAuthCta = () => (user ? navigate('/dashboard') : startCognito('signup'));

  // Scroll-reveal for .reveal blocks (the mock's inline IntersectionObserver, made React-safe).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll('.reveal');

    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Track clicks scroll to the matching section. preventDefault keeps react-router out of it,
  // while the href is left in place so middle-click and keyboard focus still behave.
  const scrollToId = (id) => (e) => {
    e.preventDefault();
    setNavOpen(false); // no-op above 800px; closes the mobile menu below it
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="pricing-page" ref={rootRef}>
      <header className="masthead">
        <div className="wrap">
          <nav>
            {/* Plain img rather than MUI — this page's stylesheet has a universal reset that
                fights MUI's generated styles. Sized in Pricing.css. */}
            <Link to="/" className="site-logo" aria-label="ReVisionari home">
              <img src="/images/revisionari-logo.png" alt="ReVisionari" />
            </Link>
            {/* Mirrors the Home page's tab set/order — Home, About, Pricing, Community —
                rather than the design mock's own nav. Community has no route yet, matching
                Home's treatment. */}
            <div className={`nav-links${navOpen ? ' open' : ''}`} id="pricing-nav-links">
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); setNavOpen(false); navigate('/'); }}
              >
                Home
              </a>
              <a
                href="/about"
                onClick={(e) => { e.preventDefault(); setNavOpen(false); navigate('/about'); }}
              >
                About
              </a>
              <a
                href="#top"
                onClick={(e) => {
                  e.preventDefault();
                  setNavOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Pricing
              </a>
              <a
                href="/community"
                onClick={(e) => {
                  e.preventDefault();
                  setNavOpen(false);
                  navigate('/community');
                }}
              >
                Community
              </a>
            </div>
            <button type="button" className="nav-cta" onClick={handleAuthCta}>
              Login/Sign up
            </button>
            <button
              type="button"
              className={`nav-toggle${navOpen ? ' open' : ''}`}
              aria-expanded={navOpen}
              aria-controls="pricing-nav-links"
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setNavOpen((open) => !open)}
            >
              <span />
              <span />
              <span />
            </button>
          </nav>
        </div>

        <div className="ticker">
          <div className="ticker-track">
            {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
              <span key={i}>
                {item.roman && <em>{item.roman}</em>}
                {item.text}
              </span>
            ))}
          </div>
        </div>

        {/* Book printing has its own estimator. The dedicated page is a separate story —
            for now this points at the existing quote calculator. */}
        <div className="wrap book-pricing-row">
          <button type="button" className="book-pricing-btn" onClick={() => navigate('/quote')}>
            Book Pricing →
          </button>
        </div>

        <div className="wrap" style={{ position: 'relative' }}>
          <div className="glow g1" />
          <div className="glow g2" />
          <div className="glow g3" />
          <div className="hero">
            <div className="stamp">
              <span>Revised<br />Est. 2026</span>
              <div className="stamp-inner"><b>R</b></div>
            </div>
            <div className="hero-grid">
              <div>
                <span className="eyebrow">Membership pricing</span>
                <h1>
                  A <span className="strike-wrap">manuscript</span>{' '}
                  <span className="insert">tiered publishing ecosystem</span>
                </h1>
                <p className="hero-sub">
                  Three tracks, three tiers each. Whether you read, edit, or write, your membership
                  grows with how deeply you&rsquo;re involved.
                </p>
                <div className="track-chips">
                  {TRACKS.map((t) => (
                    <div className="chip" key={t.id}>
                      <span className="dot" />
                      {t.name} · {t.role}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ============ THE SERVICE — cards jump to each track's free dyn-card ============ */}
      <section className="service-note" id="service">
        <div className="wrap">
          <div className="section-head reveal">
            <div>
              <span className="eyebrow">The service</span>
            </div>
            <div>
              <h2>Four modes of engagement</h2>
              <p>
                Each track carries its own tiered access, set to how involved a member chooses to
                be — including a managed track for schools and organizations.
              </p>
            </div>
          </div>

          <div className="track-cards reveal">
            {[...TRACKS, INSTITUTION].map((t) => {
              const plevels = t.plevels || [
                { label: t.free.name, value: 'Free' },
                { label: t.paid.name, value: `${t.paid.price}/mo` },
              ];
              return (
                <a key={t.id} href={`#${t.id}`} className="track-card" onClick={scrollToId(t.id)}>
                  <span className="num">{t.num}</span>
                  <h3>{t.name}</h3>
                  <span className="role">{t.role}</span>
                  <p>{t.cardBlurb}</p>
                  <div className="plevels">
                    {plevels.map((p) => (
                      <span key={p.label}>
                        {p.label} <b>{p.value}</b>
                      </span>
                    ))}
                  </div>
                </a>
              );
            })}
          </div>

          <p className="service-fine">
            Free tier on every track, no card required · paid plans include physical book access at
            print-plus-shipment cost · cancel anytime · prices in USD
          </p>
        </div>
      </section>

      {/* ============ WHY REVISIONARI ============ */}
      <section className="matrix-section" id="why">
        <div className="wrap">
          <div className="section-head reveal">
            <div><span className="eyebrow">Why ReVisionari</span></div>
            <div>
              <h2>One subscription, not five separate bills</h2>
              <p>Most tools solve one piece of publishing. Here&rsquo;s what the old way actually costs compared to a ReVisionari plan.</p>
            </div>
          </div>

          <div className="matrix-wrap reveal">
            <table className="matrix">
              <thead>
                <tr>
                  <th>What you need</th>
                  <th>The old way</th>
                  <th>With ReVisionari</th>
                </tr>
              </thead>
              <tbody>
                {WHY_ROWS.map((row) => (
                  <tr key={row.label} className={row.institution ? 'institution-row' : undefined}>
                    <td className="rowlabel">{row.label}</td>
                    <td><span>{row.old}</span></td>
                    <td><span>{row.withRV}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="service-fine" style={{ color: 'var(--gray)' }}>
            Vellum, Atticus, and Patreon prices current as of August 2026 — confirm before
            publishing if this page goes live after a pricing update on their end.
          </p>
        </div>
      </section>

      {/* ============ PUBLISHING RIGHTS ============ */}
      <section className="rights-section" id="rights">
        <div className="wrap">
          <div className="section-head reveal">
            <div><span className="eyebrow">Publishing</span></div>
            <div>
              <h2>Two ways to publish through ReVisionari</h2>
              <p>
                Every book published through the platform includes ISBN registration, U.S.
                Copyright Office filing, and a royalty share — the only choice is how much service
                comes with it.
              </p>
            </div>
          </div>

          <div className="tiers duo reveal" style={{ marginBottom: 50 }}>
            {RIGHTS_TIERS.map((tier) => (
              <div key={tier.id} className={`tier-card${tier.featured ? ' featured' : ''}`}>
                <span className="tier-flag" style={tier.flagStyle}>{tier.flag}</span>
                <h4>{tier.name}</h4>
                <span className="tier-role">{tier.role}</span>
                <div className="tier-price">
                  <span className="amt">{tier.price}</span>
                  <span className="per">{tier.per}</span>
                </div>
                <p className="tier-desc">{tier.desc}</p>
                <ul className="tier-feats">
                  {tier.feats.map((f) => (
                    <li key={f}><span className="mark">✓</span>{f}</li>
                  ))}
                  {tier.locked && (
                    <li style={{ color: 'var(--gray)' }}>
                      <span className="mark" style={{ color: 'var(--gray)' }}>✕</span>{tier.locked}
                    </li>
                  )}
                </ul>
                <div className="tier-terms">
                  Royalty: <b>{tier.royalty}</b> · Term: <b>{tier.term}</b>
                </div>
                <button type="button" className="tier-cta" onClick={() => startCognito('signup')}>
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>

          <div className="rights-grid reveal">
            {RIGHTS_STATS.map((stat) => (
              <div className="rights-stat" key={stat.lbl}>
                <span className="num">{stat.num}{stat.unit && <span style={{ fontSize: 20 }}>{stat.unit}</span>}</span>
                <span className="lbl">{stat.lbl}</span>
                <p>{stat.text}</p>
              </div>
            ))}
          </div>

          <div className="matrix-wrap reveal">
            <table className="matrix">
              <thead>
                <tr>
                  <th>Deal type</th>
                  <th>Author keeps</th>
                  <th>Rights term</th>
                  <th>Exit</th>
                </tr>
              </thead>
              <tbody>
                {RIGHTS_TABLE.map((row) => (
                  <tr key={row.label} className={row.institution ? 'institution-row' : undefined}>
                    <td className="rowlabel">{row.label}</td>
                    <td><b>{row.keeps}</b></td>
                    <td><span>{row.term}</span></td>
                    <td><span>{row.exit}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rights-note reveal">
            <b>Scope of the grant:</b> either path&rsquo;s exclusive license covers eBook and print
            only. Audiobook and translation rights stay with you — pursue them independently,
            including through the Collaborator marketplace, unless you separately negotiate a
            supplemental agreement with Company.
          </div>

          <div className="rights-note reveal">
            <b>After you publish:</b> if you&rsquo;re on Author Access, your subscription steps down
            to a flat $9.99/month Published Author rate — and is waived entirely once your book&rsquo;s
            trailing 12-month royalties clear $1,250. You stop paying full subscription price on top
            of the royalty share once your book is actually earning.
          </div>

          <p className="rights-legal reveal">
            This is a summary for informational purposes. The binding terms live in your executed
            Author Publishing Agreement (Form AGR-1) and the ReVisionari Royalty Terms and
            Conditions incorporated by reference. Figures for KDP, Traditional, Kindle Press, and
            ACX are industry-standard terms as of August 2026 and may not reflect every individual
            contract.
          </p>
        </div>
      </section>

      {/* ============ MEMBERSHIP CARDS — free row + paid Access row ============ */}
      <section className="matrix-section" id="matrix">
        <div className="wrap">
          <div className="section-head reveal">
            <div><span className="eyebrow">Every track, one table</span></div>
            <div>
              <h2>Free and paid, side by side</h2>
              <p>One free tier and one paid Access tier per track, compared directly — plus what&rsquo;s à la carte and what&rsquo;s institutional.</p>
            </div>
          </div>

          <span className="dyn-row-tag">Free tier — every track</span>
          <div className="dyn-grid reveal">
            {TRACKS.map((t) => (
              <div className="dyn-card dyn-card--free" id={t.id} key={t.id}>
                <TrackIconBg id={t.id} />
                <div className="dyn-topline">
                  <span className="dyn-badge">{t.roman}</span>
                  <span className="dyn-label">{t.name}</span>
                </div>
                <div className="dyn-price"><span className="free-tag">FREE</span><span className="per">forever</span></div>
                <h3>{t.free.name}</h3>
                <span className="dyn-role">{t.free.role}</span>
                <ul className="dyn-feats">
                  {t.free.feats.map((f, i) => (
                    <li key={f} className={i === t.free.capIndex ? 'cap' : undefined}>{f}</li>
                  ))}
                </ul>
                <button type="button" className="dyn-cta" onClick={() => startCognito('signup')}>
                  {t.free.ctaLabel}
                </button>
              </div>
            ))}
          </div>

          <span className="dyn-row-tag paid">Paid — Access tier</span>
          <div className="dyn-grid reveal">
            {TRACKS.map((t) => (
              <div className={`dyn-card ${t.dynClass}`} key={t.id}>
                <TrackIconBg id={t.id} />
                <div className="dyn-topline">
                  <span className="dyn-badge">{t.roman}</span>
                  <span className="dyn-label">{t.name}</span>
                </div>
                <div className="dyn-price"><span className="amt">{t.paid.price}</span><span className="per">/mo billed monthly</span></div>
                <h3>{t.paid.name}</h3>
                <span className="dyn-role">{t.paid.role}</span>
                <ul className="dyn-feats">
                  {t.paid.feats.map((f, i) => (
                    <li key={f} className={i === t.paid.capIndex ? 'cap' : undefined}>{f}</li>
                  ))}
                </ul>
                <button type="button" className="dyn-cta" onClick={() => startCognito('signup')}>
                  {t.paid.ctaLabel}
                </button>
              </div>
            ))}
          </div>

          <div className="dyn-bottom reveal">
            <div className="dyn-bottom-half">
              <div className="dyn-bottom-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19V6a2 2 0 0 1 2-2h9l5 5v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
                  <path d="M14 4v5h5" />
                </svg>
              </div>
              <div className="dyn-bottom-text">
                <b>À la carte, Author track only</b>
                <span>No-Frills — $129.99 + 15% royalty, 24 months &nbsp;·&nbsp; Full-Service — $199.99 + 35% royalty, 24 months</span>
              </div>
              <a href="#rights" className="dyn-bottom-link" onClick={scrollToId('rights')}>See publishing terms →</a>
            </div>
            <div className="dyn-bottom-half">
              <div className="dyn-bottom-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18" />
                  <path d="M5 21V10l7-6 7 6v11" />
                  <path d="M9 21v-6h6v6" />
                </svg>
              </div>
              <div className="dyn-bottom-text">
                <b>Custom per-seat workspace</b>
                <span>{INSTITUTION.matrixSummary}</span>
              </div>
              <a href="#institution" className="dyn-bottom-link" onClick={scrollToId('institution')}>See institution details →</a>
            </div>
          </div>

          <p className="track-desc reveal" style={{ marginTop: 22, fontSize: 14, color: 'var(--gray)' }}>
            Project and cover caps are soft limits — downgrading never deletes existing work; it
            only pauses creating new ones past your plan&rsquo;s limit.
          </p>
        </div>
      </section>

      {/* ============ INSTITUTION — scroll target ============ */}
      <section className="inst-section" id={INSTITUTION.id}>
        <div className="wrap">
          <div className="inst-copy reveal">
            <span className="eyebrow">Institution</span>
            <h2>Built for schools and organizations</h2>
            <p>{INSTITUTION.blurb}</p>
            <ul className="inst-feats">
              {INSTITUTION.feats.map((f) => (
                <li key={f}>
                  <span className="mark">✓</span>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="inst-card reveal">
            <span className="tag">Per seat / month · custom billing</span>
            <div className="inst-price">
              <span className="amt">{INSTITUTION.price}</span>
              <span className="per">{INSTITUTION.per}</span>
            </div>
            <div className="inst-price-note">est. starting price · 20-seat minimum</div>
            <div className="inst-price-note" style={{ marginTop: 2 }}>
              $300/mo minimum commitment
            </div>
            <button type="button" className="inst-cta" onClick={() => navigate('/quote')}>
              Request a quote →
            </button>
          </div>
        </div>
      </section>

      {/* ============ JOIN ============ */}
      <section className="join" id="join">
        <div className="wrap">
          <span className="eyebrow">Join the story</span>
          <h2>Every reader, collaborator, and author has a role to play</h2>
          <p>Choose your track and start your journey.</p>

          <div className="join-tracks">
            {TRACKS.map((t) => (
              <div key={t.id} className="join-track" onClick={scrollToId(t.id)}>
                <span className="rn">{t.roman}</span>
                <h4>{t.name}</h4>
                <div className="from">Free to start</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
